import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, LogOut } from "lucide-react";
import { motion } from "framer-motion";
import { EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("acs_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("acs_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError("Mot de passe incorrect.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("acs_admin_token");
    setToken("");
    setLeads(null);
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLeads(r.data))
      .catch(() => {
        sessionStorage.removeItem("acs_admin_token");
        setToken("");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-black transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour au site
          </Link>
          <p className="font-sans font-extrabold tracking-tight text-slate-900">
            ACS<span className="text-slate-400 font-medium"> Corporate</span>
            <span className="ml-3 font-mono text-xs font-normal uppercase tracking-[0.2em] text-slate-400">Admin</span>
          </p>
          {token && (
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-black transition-colors">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          )}
        </div>
      </header>

      {!token ? (
        <div className="flex items-center justify-center px-6 py-32">
          <motion.form
            onSubmit={login}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            data-testid="admin-login-form"
            className="w-full max-w-md bg-white border border-gray-200 rounded-2xl p-10"
          >
            <Lock className="w-8 h-8 text-black" strokeWidth={1.25} />
            <h1 className="mt-6 font-sans font-extrabold text-3xl font-medium text-slate-900">Espace sécurisé</h1>
            <p className="mt-3 text-sm text-gray-600">Accès réservé — saisie du mot de passe administrateur.</p>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              placeholder="Mot de passe"
              className="mt-8 w-full bg-white border-b border-gray-300 focus:border-black outline-none py-3 text-slate-900 placeholder:text-gray-400 transition-colors"
            />
            {error && (
              <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="mt-8 w-full bg-black text-white px-8 py-4 rounded-full font-medium hover:bg-slate-800 transition-colors duration-300"
            >
              Accéder aux demandes
            </button>
          </motion.form>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
          <h1 className="font-sans font-extrabold text-4xl font-medium text-slate-900">Demandes reçues</h1>
          <p className="mt-3 text-sm text-gray-600 font-mono uppercase tracking-[0.2em]">
            {leads ? `${leads.length} dossier${leads.length > 1 ? "s" : ""}` : "Chargement…"}
          </p>
          <div className="mt-10 bg-white border border-gray-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-gray-200 text-left font-mono text-xs uppercase tracking-[0.15em] text-slate-500">
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">Objet</th>
                  <th className="px-6 py-4">Contact</th>
                  <th className="px-6 py-4">Email / Tél</th>
                  <th className="px-6 py-4">Cabinet</th>
                  <th className="px-6 py-4">Fonction</th>
                  <th className="px-6 py-4">SIRET / CP</th>
                  <th className="px-6 py-4">Contexte</th>
                </tr>
              </thead>
              <tbody>
                {leads && leads.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-gray-500" data-testid="admin-leads-empty">
                      Aucune demande pour le moment.
                    </td>
                  </tr>
                )}
                {leads &&
                  leads.map((l) => (
                    <tr key={l.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {new Date(l.created_at).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-6 py-4 text-slate-900 font-medium whitespace-nowrap">
                        {l.objet || "—"}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900 whitespace-nowrap">
                        {l.prenom} {l.nom}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {l.email}
                        <br />
                        {l.telephone}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{l.cabinet}</td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{l.fonction}</td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap">
                        {l.siret}
                        <br />
                        {l.code_postal}
                      </td>
                      <td className="px-6 py-4 text-gray-600 max-w-xs">{l.contexte}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
